import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import {
  fetchAllowedMenus,
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  CreateCampaigns,
  fetchCampaignTypes,
} from "../Services/APIService";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import OverlayLoader from "./OverlayLoader";
import Select from "react-select";

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
  createdBy: number;
  createdOn: string;
}

interface SubMediaSource {
  id: number;
  name: string;
  mediaSourceID: number;
  isActive: boolean;
  createdBy: number;
  createdOn: Date;
}

interface CreateCampaign {
  tenantId: number;
  campaignName: string;
  description: string;
  fromDate: Date;
  toDate: Date;
  mediaId: number;
  subMediumId: number;
  mediaIdentifierKey: string;
  campaignImage: string;
  createdBy: number;
  campaignTypeId?: number;
}

interface ValidationErrors {
  campaignName?: string;
  description?: string;
  mediaSourceId?: string;
  subMediaSourceId?: string;
  fromDate?: string;
  toDate?: string;
  campaignImage?: string;
  campaignTypeId?: string;
}

interface MediaSourceOption {
  value: number;
  label: string;
}

interface campaignTypeOption {
  value: number;
  label: string;
}

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCampaign) => void;
  onSuccess?: (data: UpdateCampaign) => void;
}

interface UpdateCampaign {
  campaignId: number;
  campaignName: string;
  description: string;
  fromDate: Date;
  toDate: Date;
  isActive: boolean;
  mediaId: number;
  subMediumId: number;
  mediaIdentifierKey: string;
  campaignImage: string;
  modifiedBy: number;
  campaignTypeId?: number;
  isAssignTabShown?: boolean;
}

const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onSuccess,
}) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mediaSource, setMediaSource] = useState<MediaSource[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subMediaSource, setSubMediaSource] = useState<SubMediaSource[]>([]);
  const [campaignImageFile, setCampaignImageFile] = useState<File | null>(null);
  const [invalidFile, setInvalidFile] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [mediaSourceOptions, setmediaSourceOptions] = useState<
    MediaSourceOption[]
  >([]);
  const [campaignTypeeOptions, setcampaignTypeeOptions] = useState<
    campaignTypeOption[]
  >([]);

  const [campaignTypes, setCampaignTypes] = useState<
    { id: number; name: string }[]
  >([]);
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    campaignName: "",
    mediaSourceId: "",
    subMediaSourceId: "",
    fromDate: "",
    toDate: "",
    description: "",
    campaignTypeId: "",
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getMenus();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const mediaSourceResponse = await fetchMeadiaSourceData();
        if (!mediaSourceResponse)
          throw new Error("Failed to fetch media source data");
        const activeMediaSource = mediaSourceResponse.filter(
          (source: MediaSource) => source.isActive === true
        );
        const option = activeMediaSource.map((source) => ({
          value: source.sourceId,
          label: source.sourceName,
        }));
        setMediaSource(activeMediaSource);
        setmediaSourceOptions(option);

        const subMediaSourceResponse = await fetchAllSubMediaSource();
        if (!subMediaSourceResponse)
          throw new Error("Failed to fetch sub media source data");
        const activeSubMediaSource = subMediaSourceResponse.filter(
          (sub: SubMediaSource) => sub.isActive === true
        );
        setSubMediaSource(activeSubMediaSource);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await fetchCampaignTypes();
        const activeCampaignType = response.campaignTypes.filter(
          (type: any) => type.isActive
        );
        console.log("Fetched campaign types:", response);
        setCampaignTypes(activeCampaignType);
        const option = activeCampaignType.map((cmpaignType: any) => ({
          value: cmpaignType.id,
          label: cmpaignType.name,
        }));
        setcampaignTypeeOptions(option);
      } catch (error) {
        console.error("Error fetching campaign types:", error);
        setCampaignTypes([]);
      }
    };
    fetchTypes();
  }, []);

  const validateFileExtension = (fileName: string): boolean => {
    const validExtensions = [".jpg", ".jpeg", ".png"];
    const fileExtension = fileName
      .slice(fileName.lastIndexOf("."))
      .toLowerCase();
    return validExtensions.includes(fileExtension);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];

    if (file) {
      const isValidExtension = validateFileExtension(file.name);

      if (!isValidExtension) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          campaignImage: "Only .jpeg, .jpg, and .png files are allowed",
        }));
        setInvalidFile(true);
        setCampaignImageFile(file);
      } else {
        setCampaignImageFile(file);
        setInvalidFile(false);
        setErrors((prevErrors) => ({
          ...prevErrors,
          campaignImage: undefined,
        }));
      }
    } else {
      setCampaignImageFile(null);
      setInvalidFile(false);
      setErrors((prevErrors) => ({ ...prevErrors, campaignImage: undefined }));
    }
  };

  const handleRemoveImage = () => {
    setCampaignImageFile(null);
    setInvalidFile(false);
    setErrors((prevErrors) => ({ ...prevErrors, campaignImage: undefined }));
    const fileInput = document.getElementById(
      "campaignImageInput"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64Content = base64String.split(",")[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.campaignName.trim()) {
      newErrors.campaignName = "Campaign name is required";
      isValid = false;
    }

    if (!formData.campaignTypeId) {
      newErrors.campaignTypeId = "Campaign type is required";
      isValid = false;
    }

    if (!formData.mediaSourceId) {
      newErrors.mediaSourceId = "Media Source is required";
      isValid = false;
    }
    if (!formData.subMediaSourceId) {
      newErrors.subMediaSourceId = "Sub Media Source is required";
      isValid = false;
    }
    if (!formData.fromDate) {
      newErrors.fromDate = "From Date is required";
      isValid = false;
    }
    if (!formData.toDate) {
      newErrors.toDate = "To Date is required";
      isValid = false;
    }
    if (formData.fromDate && formData.toDate) {
      const fromDateObj = new Date(formData.fromDate);
      const toDateObj = new Date(formData.toDate);
      if (toDateObj < fromDateObj) {
        newErrors.toDate = "To Date cannot be earlier than From Date";
        isValid = false;
      }
    }
    if (invalidFile) {
      newErrors.campaignImage = "Only .jpeg, .jpg, and .png files are allowed";
      isValid = false;
    }

    console.log("Validation errors:", newErrors);
    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    console.log(`Changing ${name} to ${value}`); // Debug
    if (value.startsWith(" ")) return;

    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, [name]: value };
      if (name === "mediaSourceId") updatedFormData.subMediaSourceId = "";
      return updatedFormData;
    });

    if (errors[name as keyof ValidationErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    const userId = localStorage.getItem("UserId");
    const tenantId = localStorage.getItem("tenantId");
    setIsSubmitting(true);
    let campaignImageData = "";
    if (campaignImageFile) {
      try {
        campaignImageData = await fileToBase64(campaignImageFile);
      } catch (error) {
        console.error("Error converting campaign image to Base64:", error);
        toast.error("Failed to process the campaign image");
        setIsSubmitting(false);
        return;
      }
    }

    const body: CreateCampaign = {
      campaignName: formData.campaignName,
      mediaId: parseInt(formData.mediaSourceId),
      subMediumId: parseInt(formData.subMediaSourceId || "0"),
      description: formData.description,
      fromDate: new Date(formData.fromDate),
      toDate: new Date(formData.toDate),
      tenantId: tenantId ? parseInt(tenantId) : 0,
      createdBy: userId ? parseInt(userId) : 0,
      campaignImage: campaignImageData,
      mediaIdentifierKey: "224f8774-d7c4-45a2-b9da-4bd2d350ac68",
      campaignTypeId: formData.campaignTypeId
        ? parseInt(formData.campaignTypeId)
        : undefined,
    };

    try {
      const result = await CreateCampaigns(body);
      console.log("Create campaign result:", result);
      if (result.isCreated) {
        const mappingData: UpdateCampaign = {
          campaignId: result.data.campaigN_ID || 0,
          campaignName: result.data.campaigN_NAME || "",
          description: result.data.description || "",
          fromDate: result.data.froM_DATE || new Date(),
          toDate: result.data.tO_DATE || new Date(),
          isActive: result.data.iS_ACTIVE || false,
          mediaId: result.data.mediA_ID || 0,
          subMediumId: result.data.suB_MEDIUM_ID || 0,
          mediaIdentifierKey: result.data.mediA_IDENTIFIER_KEY || "",
          campaignImage: result.data.campaigN_IMAGE || "",
          modifiedBy: result.data.modifieD_BY || 0,
          campaignTypeId: result.data.campaigN_TYPE_ID || undefined,
        };
        toast.success(result.message || "Campaign created successfully!");
        resetForm();
        if (onSuccess) {
          onSuccess(mappingData);
        }
        onClose();
        setTimeout(() => {
          navigate("/campaignView", {
            state: {
              refresh: true,
              timestamp: Date.now(),
            },
            replace: true,
          });
        }, 500);
      } else {
        toast.error(result.message || "Campaign creation failed");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error occurred during Campaign creation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      campaignName: "",
      mediaSourceId: "",
      subMediaSourceId: "",
      fromDate: "",
      toDate: "",
      description: "",
      campaignTypeId: "",
    });
    setCampaignImageFile(null);
    setInvalidFile(false);
    setErrors({});
    const fileInput = document.getElementById(
      "campaignImageInput"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleClose = () => {
    setFormData({
      campaignName: "",
      mediaSourceId: "",
      subMediaSourceId: "",
      fromDate: "",
      toDate: "",
      description: "",
      campaignTypeId: "",
    });
    setErrors({});
    onClose();
  };

  const iconButtonStyle: React.CSSProperties = {
    top: "36%",
    right: "10px",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    fontSize: "16px",
    lineHeight: "1",
    color: "#495057",
    position: "absolute",
  };

  const handleMediaSourceSelectChange = (
    selectedOption: MediaSourceOption | null
  ) => {
    if (!selectedOption) {
      setFormData({
        ...formData,
        mediaSourceId: "", // or null/undefined based on your needs
      });
      return;
    }

    setFormData({
      ...formData,
      mediaSourceId: selectedOption.value.toString(), // Use .value, not .label
    });
  };

  const handelCamapignTypeChanges = (
    selectedOption: campaignTypeOption | null
  ) => {
    if (!selectedOption) {
      setFormData({
        ...formData,
        campaignTypeId: "", // or null/undefined based on your needs
      });
      return;
    }

    setFormData({
      ...formData,
      campaignTypeId: selectedOption.value.toString(), // Use .value, not .label
    });
  };

  const selectedOption =
    mediaSourceOptions.find(
      (option) => option.value === parseInt(formData.mediaSourceId)
    ) || null;

  const selectedCampaignTypeOption =
    campaignTypeeOptions.find(
      (option) => option.value === parseInt(formData.campaignTypeId)
    ) || null;

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <>
      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Create Campaign</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>
          <div className="modal-body-custom">
            <div className="modal-body-content">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="campaignName">
                      Campaign Name{requiredField}
                    </label>
                    <div className="input-with-icon">
                      <span className="input-icon icon-campaign"></span>
                      <input
                        name="campaignName"
                        placeholder="Campaign Name"
                        value={formData.campaignName}
                        onChange={handleChange}
                        className={`form-control-custom ${
                          errors.campaignName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.campaignName && (
                        <div className="invalid-feedback text-danger">
                          {errors.campaignName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="campaignTypeId">
                      Campaign Type{requiredField}
                    </label>
                    <Select
                      options={campaignTypeeOptions}
                      value={selectedCampaignTypeOption} // Pass the full option object, not just the ID
                      onChange={handelCamapignTypeChanges}
                      className="basic-single-select"
                      classNamePrefix="select"
                      placeholder="Select Campaign Type"
                      isLoading={isLoading}
                      isSearchable={true} // Enable search functionality
                      isClearable={true} // Optional: allows clearing the selection
                      id="campaignTypeId"
                      name="campaignTypeId"
                    />
                    {errors.campaignTypeId && (
                      <div className="invalid-feedback text-danger">
                        {errors.campaignTypeId}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="mediaSourceId">
                      Media Source{requiredField}
                    </label>
                    <Select
                      options={mediaSourceOptions}
                      value={selectedOption} // Pass the full option object, not just the ID
                      onChange={handleMediaSourceSelectChange}
                      className="basic-single-select"
                      classNamePrefix="select"
                      placeholder="Select Source"
                      isLoading={isLoading}
                      isSearchable={true} // Enable search functionality
                      isClearable={true} // Optional: allows clearing the selection
                      id="mediaSourceId"
                      name="mediaSourceId"
                    />
                    {errors.mediaSourceId && (
                      <div className="invalid-feedback text-danger">
                        {errors.mediaSourceId}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="subMediaSourceId">
                      Sub Media Source{requiredField}
                    </label>
                    <select
                      name="subMediaSourceId"
                      value={formData.subMediaSourceId || ""}
                      onChange={handleChange}
                      className={`form-select form-control-lg ${
                        errors.subMediaSourceId ? "is-invalid" : ""
                      }`}
                    >
                      <option value="">Select Sub Media Source</option>
                      {subMediaSource
                        .filter(
                          (sub) =>
                            sub.mediaSourceID ===
                            parseInt(formData.mediaSourceId)
                        )
                        .map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ))}
                    </select>
                    {errors.subMediaSourceId && (
                      <div className="invalid-feedback text-danger">
                        {errors.subMediaSourceId}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-6 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="fromDate">
                      From Date{requiredField}
                    </label>
                    <input
                      type="datetime-local"
                      name="fromDate"
                      value={formData.fromDate}
                      onChange={handleChange}
                      className={`form-control-custom1 form-control-custom-lg ${
                        errors.fromDate ? "is-invalid" : ""
                      }`}
                    />
                    {errors.fromDate && (
                      <div className="invalid-feedback text-danger">
                        {errors.fromDate}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-6 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="toDate">
                      To Date{requiredField}
                    </label>
                    <input
                      type="datetime-local"
                      name="toDate"
                      value={formData.toDate}
                      onChange={handleChange}
                      className={`form-control-custom1 form-control-custom-lg ${
                        errors.toDate ? "is-invalid" : ""
                      }`}
                    />
                    {errors.toDate && (
                      <div className="invalid-feedback text-danger">
                        {errors.toDate}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="campaignImageInput">
                      Campaign Image
                    </label>
                    <div className="position-relative">
                      <input
                        id="campaignImageInput"
                        type="file"
                        className={`form-control-custom1 form-control-custom-lg ${
                          errors.campaignImage ? "is-invalid" : ""
                        }`}
                        onChange={handleFileChange}
                        accept="image/jpeg,image/jpg,image/png"
                        style={{
                          paddingRight: campaignImageFile ? "30px" : "12px",
                        }}
                      />
                      {campaignImageFile && (
                        <>
                          <div className="form-text">
                            Selected: {campaignImageFile.name}
                          </div>
                          <button
                            type="button"
                            className="position-absolute p-0"
                            style={iconButtonStyle}
                            onClick={handleRemoveImage}
                          >
                            ×
                          </button>
                        </>
                      )}
                    </div>
                    {errors.campaignImage && (
                      <div className="invalid-feedback text-danger d-block">
                        {errors.campaignImage}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="description">
                      Description
                    </label>
                    <textarea
                      name="description"
                      placeholder="Description"
                      value={formData.description}
                      onChange={handleChange}
                      className={`form-control-custom-textarea ${
                        errors.description ? "is-invalid" : ""
                      }`}
                    />
                    {errors.description && (
                      <div className="invalid-feedback text-danger">
                        {errors.description}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            <div className="modal-footer-custom">
              <button
                type="button"
                onClick={handleClose}
                className="btn-custom btn-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn-custom btn-primary-custom"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
        <OverlayLoader
          loading={isLoading}
          message="Loading Data"
          submessage="This might take a moment..."
        />
      </div>
    </>
  );
};

export default CreateCampaignModal;
